import { useState } from 'react';
import { HandThumbUpIcon, HandThumbDownIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '@lib/api/client';
import { CTA, GHOST } from '@components/ui/designTokens';
import { captureScreenshotBase64 } from '../lib/captureScreenshot';

/**
 * Barra en el borde inferior que PERSISTE tras cerrar el modal: pregunta si la
 * solución funcionó. El 👍 valida y crea el playbook; el 👎 lo registra.
 *
 * Va centrada abajo y en una sola fila: es transitoria y no debe tapar tarjetas
 * ni columnas del contenido. Vestida como una tarjeta del sistema (borde fino
 * #ececec, radio 14px) con el navy como acción primaria.
 */
export function SupportFeedbackToast({
  interactionId,
  topic,
  onClose,
}: {
  interactionId: string;
  topic?: string;
  onClose: () => void;
}) {
  const [done, setDone] = useState<'up' | 'down' | null>(null);
  const [sending, setSending] = useState<'up' | 'down' | null>(null);

  async function vote(helpful: 'up' | 'down') {
    if (sending || done) return;
    setSending(helpful);
    const data_base64 = await captureScreenshotBase64();
    try {
      await apiClient.post('/support/feedback', {
        interactionId,
        helpful,
        image: data_base64 ? { mime_type: 'image/jpeg', data_base64 } : undefined,
      });
    } catch {
      // best-effort
    } finally {
      setSending(null);
      setDone(helpful);
      setTimeout(onClose, 2800);
    }
  }

  return (
    <div
      data-support-ui
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      className="fixed bottom-6 left-1/2 z-[80] w-max max-w-[92vw] -translate-x-1/2 animate-support-in rounded-[14px] border border-[#ececec] bg-white px-4 py-2.5 font-display shadow-[0_30px_70px_-20px_rgba(10,34,51,.35)] dark:border-secondary-700 dark:bg-secondary-800"
    >
      {done ? (
        <div className="flex items-center gap-2 text-[13px] font-medium text-ink-850 dark:text-secondary-100">
          <CheckCircleIcon className="h-5 w-5 shrink-0 text-teal-600" />
          {done === 'up'
            ? '¡Gracias! Lo guardamos para ayudar más rápido la próxima.'
            : 'Gracias. Lo registramos para revisarlo.'}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
          <p className="text-[13.5px] font-medium text-ink-850 dark:text-white">
            ¿Te funcionó {topic ? <span className="font-bold">«{topic}»</span> : 'la solución'}?
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => vote('up')}
              disabled={!!sending}
              className={`${CTA} px-3.5 py-2 text-[13px]`}
            >
              <HandThumbUpIcon className="h-4 w-4" /> Sí
            </button>
            <button
              type="button"
              onClick={() => vote('down')}
              disabled={!!sending}
              className={`${GHOST} px-3.5 py-2 text-[13px] disabled:opacity-60`}
            >
              <HandThumbDownIcon className="h-4 w-4" /> No
            </button>
            <button
              onClick={onClose}
              className="ml-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-secondary-400 transition-colors hover:border-[#ececec] hover:bg-[#f6f6f7] hover:text-ink-850 dark:hover:border-secondary-600 dark:hover:bg-secondary-700"
              aria-label="Cerrar"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
